import React from "react";
import { Link } from "react-router-dom";
import { Link2, Inbox, Clipboard, FileText, ShoppingCart, Package, Tool, Shield, ExternalLink } from "react-feather";
import CollapsibleCard, { PANEL_ACCENT } from "./CollapsibleCard";
import { useGetSalesLinkedDocumentsQuery } from "../salesApi";
import { drillTarget } from "../../reports/oneStop/oneStopDrill";

// "Linked Documents" on Enquiry / Order detail (Batch166): every document that
// hangs off the enquiry and the order it became, each opening the real record.
// The drill-down reuses the One Stop report's map (DocKind == its DrillKind),
// so the targets are the same existing screens, not new routes.

const KIND = {
  SALES_ENQUIRY: { icon: Inbox, color: PANEL_ACCENT.link },
  SALES_ORDER: { icon: Clipboard, color: PANEL_ACCENT.money },
  QUOTATION: { icon: FileText, color: PANEL_ACCENT.document },
  LPO: { icon: ShoppingCart, color: PANEL_ACCENT.services },
  STOCK_OUT: { icon: Package, color: "#00838F" },
  TECHNICAL_WORK: { icon: Tool, color: PANEL_ACCENT.work },
  CUSTOMER_GUARDING: { icon: Shield, color: PANEL_ACCENT.guards },
};

const money = (n) => (n == null ? "" : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const shortDate = (v) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");

const statusStyle = (status) => {
  const s = String(status || "").toUpperCase();
  if (["APPROVED", "CONFIRMED", "COMPLETED", "CLOSED", "ACTIVE", "OPEN", "ISSUED", "RECEIVED", "PARTIALLY_RECEIVED"].includes(s))
    return { background: "#E8F5E9", color: "#1E8449" };
  if (["CANCELLED", "REJECTED", "DELETED", "LOST", "ENDED"].includes(s)) return { background: "#FDECEA", color: "#C62828" };
  if (["SUBMITTED", "PENDING", "ON_HOLD", "POSTPONED", "REVISED_SUBMITTED", "IN_PROGRESS"].includes(s))
    return { background: "#FFF4E5", color: "#B54708" };
  return { background: "#F2F4F7", color: "#475467" };
};

/** `hideKind` drops the page's own document (the enquiry on the enquiry page, the order on the order page). */
const SalesLinkedDocumentsPanel = ({ enquiryRecordGuid, hideKind }) => {
  const { data = [], isError } = useGetSalesLinkedDocumentsQuery(enquiryRecordGuid, { skip: !enquiryRecordGuid });
  if (isError) return null;
  const docs = data.filter((d) => d.docKind !== hideKind);

  return (
    <CollapsibleCard
      title="Linked Documents"
      icon={Link2}
      accent={PANEL_ACCENT.link}
      hasData={docs.length > 0}
      count={docs.length}
      summary={docs.length > 0 ? [...new Set(docs.map((d) => d.docType))].join(" · ") : null}
    >
      {docs.length === 0 ? (
        <div className="text-muted small">Nothing is linked yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Document</th>
                <th>Number</th>
                <th>Date</th>
                <th>Status</th>
                <th className="text-end">Amount</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d, i) => {
                const k = KIND[d.docKind] || { icon: FileText, color: PANEL_ACCENT.neutral };
                const Icon = k.icon;
                const target = drillTarget({
                  drillKind: d.docKind, drillGuid: d.drillGuid, drillId: d.drillId, drillBranchId: d.drillBranchId,
                });
                const label = d.docNumber || "Open";
                return (
                  <tr key={`${d.docKind}-${d.drillGuid || d.drillId || i}`} style={{ borderLeft: `3px solid ${k.color}` }}>
                    <td>
                      <span className="d-inline-flex align-items-center gap-2 fw-semibold">
                        <span
                          className="d-inline-flex align-items-center justify-content-center rounded-circle"
                          style={{ width: 24, height: 24, background: `${k.color}1F`, color: k.color }}
                        >
                          <Icon size={13} />
                        </span>
                        {d.docType}
                      </span>
                    </td>
                    <td>
                      {target ? (
                        <Link to={target} className="fw-semibold d-inline-flex align-items-center gap-1">
                          {label} <ExternalLink size={12} />
                        </Link>
                      ) : (
                        <span className="fw-semibold">{d.docNumber || "-"}</span>
                      )}
                    </td>
                    <td className="text-nowrap">{shortDate(d.docDate)}</td>
                    <td>
                      {d.status && (
                        <span className="badge rounded-pill" style={{ ...statusStyle(d.status), fontWeight: 600 }}>
                          {String(d.status).replace(/_/g, " ")}
                        </span>
                      )}
                    </td>
                    <td className="text-end fw-semibold text-nowrap">{money(d.amount)}</td>
                    <td className="small text-muted">{d.detail}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleCard>
  );
};

export default SalesLinkedDocumentsPanel;
